import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Search, MapPin, Crown } from "lucide-react";

const CATEGORIES = ["all", "Electronics", "Fashion", "Home", "Vehicles", "Collectibles", "Services", "Sports", "Art", "Other"];
const TYPES = [{ v: "all", l: "Everything" }, { v: "good", l: "Goods" }, { v: "service", l: "Services" }];

export default function Browse() {
  const [listings, setListings] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("");
  const [membersOnly, setMembersOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/listings", { params: { q: q || undefined, type, category, location: location || undefined, members_only: membersOnly || undefined } });
      setListings(data);
    } catch (e) {}
    setLoading(false);
  }, [q, type, category, location, membersOnly]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-6">Browse the exchange</h1>

      <div className="bg-white nb-border rounded-2xl p-5 nb-shadow mb-8">
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} strokeWidth={3} />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search items, services, or what people want…"
            className="w-full pl-11 pr-4 py-3 nb-border rounded-xl font-semibold outline-none focus:ring-2 focus:ring-[#E6B31E]"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={16} strokeWidth={3} />
            <input data-testid="location-filter" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Filter by location / city…"
              className="w-full pl-10 pr-4 py-2.5 nb-border rounded-xl font-semibold outline-none focus:ring-2 focus:ring-[#E6B31E]" />
          </div>
          <button data-testid="members-only-toggle" onClick={() => setMembersOnly(!membersOnly)}
            className={`flex items-center justify-center gap-1.5 font-bold text-sm rounded-xl px-4 py-2.5 border-2 border-black transition-colors ${membersOnly ? "bg-[#E6B31E] text-black" : "bg-white hover:bg-[#FBF7E9]"}`}>
            <Crown size={16} strokeWidth={3} /> Verified members only
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button key={t.v} data-testid={`filter-type-${t.v}`} onClick={() => setType(t.v)}
              className={`font-bold text-sm rounded-full px-4 py-1.5 border-2 border-black transition-colors ${type === t.v ? "bg-black text-[#E6B31E]" : "bg-white hover:bg-[#FBF7E9]"}`}>
              {t.l}
            </button>
          ))}
          <div className="w-px bg-black/10 mx-1" />
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`filter-cat-${c}`} onClick={() => setCategory(c)}
              className={`font-bold text-sm rounded-full px-4 py-1.5 border-2 border-black transition-colors ${category === c ? "bg-[#E6B31E] text-black" : "bg-white hover:bg-[#FBF7E9]"}`}>
              {c === "all" ? "All categories" : c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center font-display font-extrabold text-xl animate-pulse">Loading trades…</div>
      ) : listings.length === 0 ? (
        <div className="py-20 text-center">
          <div className="font-display font-extrabold text-2xl">No trades found</div>
          <p className="text-black/50 font-medium mt-2">Try adjusting your filters or be the first to list something.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-start" data-testid="listings-grid">
          {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      )}
    </div>
  );
}
